import { ApplicationView, useController } from '@aha-app/mvc';
import { useReadOnly } from 'slate-react';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import React from 'react';
import type {
  SharedSnippetInlineElement,
  SharedSnippetElement,
  ComponentProps,
} from '../../../types/aha-editor';
import SharedSnippetCommon from '../SharedSnippetCommon';

interface SharedSnippetBodyProps {
  token: string;
  inline: boolean;
}

type SharedSnippetProps = ComponentProps<
  SharedSnippetElement | SharedSnippetInlineElement
>;

const SharedSnippetBody: React.FC<SharedSnippetBodyProps> = ({
  token,
  inline,
}) => {
  const controller = useController(AhaEditorV2Controller);
  const snippet = SharedSnippetCommon.getSnippet(controller, token);
  const readOnly = useReadOnly();

  const Element = inline ? 'span' : 'div';
  let style: React.CSSProperties = {};

  if (!readOnly) {
    // When editable, disable pointer events so you can click the
    // snippet for editing without the snippet content interfering.
    style = {
      ...style,
      pointerEvents: 'none',
    };
  }

  if (snippet) {
    // Render the snippet using the HTML serialization to avoid sanitization issues.
    const html = SharedSnippetCommon.convertSnippetToHtml(snippet);
    return (
      <Element
        className="user-content"
        dangerouslySetInnerHTML={{ __html: html }}
        style={style}
      />
    );
  } else if (snippet === null) {
    return (
      <Element className="p-2" style={style}>
        <i className="fa-regular fa-warning" /> Could not find shared snippet.
      </Element>
    );
  } else {
    return (
      <Element className="p-2" style={style}>
        <aha-spinner />
      </Element>
    );
  }
};

const WrappedSharedSnippetBody = ApplicationView(SharedSnippetBody);

const SharedSnippet: React.FC<SharedSnippetProps> = ({
  attributes,
  element,
  children,
}) => {
  const token = element.sharedSnippet_token;
  const readOnly = useReadOnly();
  const inline = element.type === 'shared-snippet-inline';
  const Element = inline ? 'span' : 'div';

  let style: React.CSSProperties = {
    backgroundColor: 'rgba(var(--theme-editor-toolbar), 0.5)',
    flex: inline ? undefined : 1,
    maxWidth: inline ? undefined : '100%',
  };

  if (!readOnly) {
    style = {
      ...style,
      userSelect: 'none',
      border: 'dashed 2px var(--theme-editor-toolbar)',
      borderRadius: 4,
    };
  }

  return (
    <SelectionRectangle inline={inline} hasMargin={false}>
      <Element contentEditable={false} style={style} {...attributes}>
        {token ? (
          <WrappedSharedSnippetBody token={token} inline={inline} />
        ) : null}
        {children}
      </Element>
    </SelectionRectangle>
  );
};

export default SharedSnippet;
